import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { PageContainer } from "@/layouts/PageContainer";
import { PageHeader } from "@/layouts/PageHeader";
import { Share2, ChevronLeft, AlertCircle, BookOpen, Eye, Pencil } from "lucide-react";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { EmptyState } from "@/components/ui/EmptyState";

function PermissionLabel({ permission }) {
  const isEdit = permission === "edit";
  const Icon = isEdit ? Pencil : Eye;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-[0.7rem] font-medium text-muted-foreground">
      <Icon size={11} />
      {isEdit ? "Can edit" : "View only"}
    </span>
  );
}

function MemberRow({ member }) {
  const name = member.name || member.email || "Unknown";
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/30 border border-transparent">
      <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase flex items-center justify-center shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate leading-tight">
          {name}
        </p>
        {member.email && member.email !== name && (
          <p className="text-[11px] text-muted-foreground truncate leading-tight mt-0.5">
            {member.email}
          </p>
        )}
      </div>
      <PermissionLabel permission={member.permission} />
    </div>
  );
}

function ShareCard({ share }) {
  const ownerName = share.ownerName || "Unknown";
  const memberCount = share.sharedWith?.length ?? 0;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <BookOpen size={15} className="text-primary shrink-0" />
          <p className="text-sm font-semibold text-foreground truncate">
            {share.groupName || "Unnamed Report Folder"}
          </p>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          Owned by{" "}
          <span className="font-medium text-foreground">{ownerName}</span>
          {share.ownerEmail && (
            <span className="text-muted-foreground/80"> · {share.ownerEmail}</span>
          )}
        </p>
      </div>

      <div className="px-5 py-3">
        <p className="text-[0.7rem] font-semibold tracking-wide uppercase text-muted-foreground mb-2">
          Shared with {memberCount > 0 ? `(${memberCount})` : ""}
        </p>
        {memberCount === 0 ? (
          <p className="text-xs text-muted-foreground italic py-1">No members</p>
        ) : (
          <div className="space-y-1.5">
            {share.sharedWith.map((sw) => (
              <MemberRow key={String(sw.userId)} member={sw} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReportFolderShares() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/report-folder-shares`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load Report Folder shares");
      }
      const data = await res.json();
      setShares(data.shares ?? []);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="h-12 w-2/3 bg-muted animate-pulse rounded mb-8" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-2xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Link
          to="/schools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft size={14} />
          My School
        </Link>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>

      <PageHeader
        title="Active Folder Sharing"
        subtitle="See every Report Folder shared between teachers in your school. Folder owners manage their own member list — this view is read-only."
      />

      {shares.length === 0 ? (
        <EmptyState
          icon={Share2}
          title="No active sharing"
          description="When a teacher shares a Report Folder with a colleague, it will appear here so you can see who has access to what."
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-4">
            {shares.length} Report Folder{shares.length !== 1 ? "s" : ""} currently shared
          </p>
          <div className="space-y-3">
            {shares.map((share) => (
              <ShareCard key={share.groupId} share={share} />
            ))}
          </div>
        </>
      )}
    </PageContainer>
  );
}
